import React, { useState, useMemo } from 'react';
import { Box, Tabs, Tab, Alert } from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { reportApi } from '../../services/reportService';
import { useSalonContextStore } from '../../store/salonContextStore';
import { PageHeader, SectionCard, EmptyState, LoadingState } from '../../components/ui';
import { tokens } from '../../theme';
import { StatsDateRange, DateRange, RangePreset, computePresetRange } from './stats/StatsDateRange';
import { useAsyncData } from './stats/statsShared';
import { PanelTab } from './stats/PanelTab';
import { DayTab } from './stats/DayTab';
import { WeekTab } from './stats/WeekTab';
import { PeakHoursTab } from './stats/PeakHoursTab';
import { RevenueTab } from './stats/RevenueTab';
import { BookingsTab } from './stats/BookingsTab';
import { ClientsTab } from './stats/ClientsTab';
import { EmployeesTab } from './stats/EmployeesTab';

const TABS = ['Podsumowanie', 'Dzień', 'Tydzień', 'Godziny szczytu', 'Przychód', 'Wizyty', 'Klienci', 'Pracownicy'];

// Tabs that manage their own date input (Day picks a day, Week picks a week).
const SELF_DATED = new Set([1, 2]);
// Tabs powered by the shared salon-dashboard payload.
const DASHBOARD_TABS = new Set([0, 4, 5, 6]);

export const SalonStatsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [preset, setPreset] = useState<RangePreset>('month');
  const [range, setRange] = useState<DateRange>(() => computePresetRange('month'));
  const { activeSalonId } = useSalonContextStore();

  const dashboardNeeded = DASHBOARD_TABS.has(tab);
  const { data: dashboard, loading, error } = useAsyncData(
    () => {
      if (!activeSalonId || !dashboardNeeded) return Promise.resolve(null);
      return reportApi.getSalonDashboard(activeSalonId, range.dateFrom, range.dateTo);
    },
    [activeSalonId, range.dateFrom, range.dateTo, dashboardNeeded],
  );

  const showRange = useMemo(() => !SELF_DATED.has(tab), [tab]);

  const handleRange = (r: DateRange, p: RangePreset) => {
    setRange(r);
    setPreset(p);
  };

  const header = (
    <PageHeader
      breadcrumbs={[{ label: 'Analizy' }]}
      title="Statystyki salonu"
      description="Przychód, wizyty, klienci i praca zespołu w wybranym okresie."
    >
      {activeSalonId && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Widoki statystyk"
          sx={{ borderBottom: `1px solid ${tokens.line}` }}
        >
          {TABS.map((t) => (
            <Tab key={t} label={t} />
          ))}
        </Tabs>
      )}
    </PageHeader>
  );

  if (!activeSalonId) {
    return (
      <Box>
        {header}
        <SectionCard>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Wybierz salon"
            description="Statystyki pokazujemy dla jednego salonu — wybierz go z listy u góry ekranu."
          />
        </SectionCard>
      </Box>
    );
  }

  const renderTab = () => {
    if (SELF_DATED.has(tab)) {
      return tab === 1 ? <DayTab salonId={activeSalonId} /> : <WeekTab salonId={activeSalonId} />;
    }
    if (tab === 3) return <PeakHoursTab salonId={activeSalonId} range={range} />;
    if (tab === 7) return <EmployeesTab salonId={activeSalonId} range={range} />;

    // Dashboard-backed tabs
    if (loading && !dashboard) return <LoadingState variant="page" />;
    if (error) return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
    if (!dashboard) return null;

    const content =
      tab === 0 ? <PanelTab salonId={activeSalonId} range={range} data={dashboard} />
        : tab === 4 ? <RevenueTab salonId={activeSalonId} range={range} data={dashboard} />
          : tab === 5 ? <BookingsTab salonId={activeSalonId} range={range} data={dashboard} />
            : tab === 6 ? <ClientsTab salonId={activeSalonId} range={range} data={dashboard} />
              : null;

    // Przy zmianie zakresu zostaje poprzedni widok, lekko przygaszony, az przyjda nowe dane.
    return (
      <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s ease' }} aria-busy={loading || undefined}>
        {content}
      </Box>
    );
  };

  return (
    <Box>
      {header}

      {showRange && <StatsDateRange value={range} preset={preset} onChange={handleRange} />}

      {renderTab()}
    </Box>
  );
};
